import type { Question, RepeatingGroupRow } from '@submitpro/shared';
import { getVisibleRowFields } from '@submitpro/shared';
import { useState } from 'react';

// ---------------------------------------------------------------------------
// QuestionRenderer
// ---------------------------------------------------------------------------
// Renders any Question by switching on field_type. Handles validation display.
// ---------------------------------------------------------------------------

const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
] as const;

interface QuestionRendererProps {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  onSubmit?: () => void;
  error?: string;
  /** Compact mode — used for sub-questions inside a repeating-group row */
  compact?: boolean;
}

export function QuestionRenderer({
  question,
  value,
  onChange,
  onSubmit,
  error,
  compact = false,
}: QuestionRendererProps) {
  const inputClasses = compact
    ? 'w-full rounded-lg border border-slate-300 px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition'
    : 'w-full rounded-lg border border-slate-300 px-4 py-3 text-lg text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition';
  const autoFocusInput = !compact;

  // Phase 5 — optional "type a sentence instead" natural-language mode.
  // Disabled in compact (repeating-group row) mode — sub-questions aren't in
  // the flat question library the parse endpoint resolves against.
  const [nlMode, setNlMode] = useState(false);
  const [nlText, setNlText] = useState('');
  const [nlBusy, setNlBusy] = useState(false);
  const nlEligible =
    !compact &&
    ['number', 'currency', 'percentage', 'select', 'multiselect', 'boolean', 'date'].includes(
      question.field_type,
    );

  async function applyNl() {
    if (nlText.trim().length < 1) return;
    setNlBusy(true);
    try {
      const res = await fetch('/api/intake/parse-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: question.id, text: nlText }),
      });
      if (res.ok) {
        const data = (await res.json()) as { value: unknown };
        if (data.value !== null && data.value !== undefined) onChange(data.value);
      }
    } catch {
      // leave the field as-is; the prospect can answer normally
    } finally {
      setNlBusy(false);
      setNlMode(false);
      setNlText('');
    }
  }

  function renderInput() {
    switch (question.field_type) {
      case 'text':
      case 'phone':
      case 'email':
        return (
          <input
            type={
              question.field_type === 'email'
                ? 'email'
                : question.field_type === 'phone'
                  ? 'tel'
                  : 'text'
            }
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            className={inputClasses}
            autoFocus={autoFocusInput}
          />
        );

      case 'number':
        return (
          <div className="flex items-center gap-2">
            {question.prefix && <span className="text-2xl text-slate-500">{question.prefix}</span>}
            <input
              type="number"
              value={(value as number) ?? ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={question.placeholder}
              className={inputClasses}
              autoFocus={autoFocusInput}
            />
            {question.suffix && <span className="text-lg text-slate-500">{question.suffix}</span>}
          </div>
        );

      case 'currency':
        return (
          <div className="flex items-center gap-2">
            <span className="text-2xl text-slate-500">$</span>
            <input
              type="number"
              value={(value as number) ?? ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={question.placeholder ?? '0'}
              className={inputClasses}
              autoFocus={autoFocusInput}
            />
          </div>
        );

      case 'percentage':
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              value={(value as number) ?? ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
              className={inputClasses}
              autoFocus={autoFocusInput}
            />
            <span className="text-2xl text-slate-500">%</span>
          </div>
        );

      case 'select':
        return (
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClasses}
            autoFocus={autoFocusInput}
          >
            <option value="">Select one...</option>
            {question.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'multiselect': {
        // Guard: a stale draft from the previous question can briefly be a
        // non-array (e.g. number) on the first render after advancing.
        const selected = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div className="space-y-2">
            {question.options?.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, opt.value]
                      : selected.filter((v) => v !== opt.value);
                    onChange(next);
                  }}
                  className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{opt.label}</div>
                  {opt.help && <div className="text-sm text-slate-500">{opt.help}</div>}
                </div>
              </label>
            ))}
          </div>
        );
      }

      case 'boolean':
        return (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onChange(true)}
              className={`flex-1 rounded-lg border-2 px-6 py-4 text-lg font-medium transition ${
                value === true
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => onChange(false)}
              className={`flex-1 rounded-lg border-2 px-6 py-4 text-lg font-medium transition ${
                value === false
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
              }`}
            >
              No
            </button>
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={inputClasses}
            autoFocus={autoFocusInput}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            rows={5}
            className={inputClasses}
            autoFocus={autoFocusInput}
          />
        );

      case 'address': {
        const addr = (value as Record<string, string>) ?? {};
        const set = (key: string, v: string) => onChange({ ...addr, [key]: v });
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={addr.street ?? ''}
              onChange={(e) => set('street', e.target.value)}
              placeholder="Street address"
              className={inputClasses}
              autoFocus={autoFocusInput}
            />
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                value={addr.city ?? ''}
                onChange={(e) => set('city', e.target.value)}
                placeholder="City"
                className={inputClasses}
              />
              <select
                value={addr.state ?? ''}
                onChange={(e) => set('state', e.target.value)}
                className={inputClasses}
              >
                <option value="">State</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={addr.zip ?? ''}
                onChange={(e) => set('zip', e.target.value)}
                placeholder="ZIP"
                className={inputClasses}
              />
            </div>
          </div>
        );
      }

      case 'state_select': {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        return (
          <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto rounded-lg border border-slate-200 p-3">
            {US_STATES.map(([code, name]) => (
              <label
                key={code}
                className="flex items-center gap-2 rounded-md px-3 py-2 hover:bg-blue-50 cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(code)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, code]
                      : selected.filter((v) => v !== code);
                    onChange(next);
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  {code} <span className="text-slate-500">— {name}</span>
                </span>
              </label>
            ))}
          </div>
        );
      }

      case 'trade_matrix':
        return (
          <TradeMatrixInput
            value={value as TradeRow[]}
            onChange={onChange as (v: TradeRow[]) => void}
          />
        );

      case 'repeating_group':
        return (
          <RepeatingGroupInput
            question={question}
            value={value as RepeatingGroupRow[] | undefined}
            onChange={onChange as (v: RepeatingGroupRow[]) => void}
          />
        );

      case 'file':
        return (
          <input
            type="file"
            onChange={(e) => onChange(e.target.files?.[0])}
            className={inputClasses}
          />
        );

      default:
        return <div className="text-red-600">Unsupported field type: {question.field_type}</div>;
    }
  }

  if (compact) {
    return (
      <div className="space-y-1.5">
        <div className="block text-sm font-medium text-slate-700">
          {question.text}
          {question.required && <span className="ml-0.5 text-red-500">*</span>}
        </div>
        {question.subtext && <p className="text-xs text-slate-500">{question.subtext}</p>}
        {renderInput()}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900 leading-snug">
          {question.text}
          {question.required && <span className="ml-1 text-red-500">*</span>}
        </h2>
        {question.subtext && <p className="mt-2 text-slate-600">{question.subtext}</p>}
      </div>

      {nlEligible && nlMode ? (
        <div className="space-y-3">
          <textarea
            value={nlText}
            onChange={(e) => setNlText(e.target.value)}
            rows={2}
            placeholder="Answer in your own words…"
            className={inputClasses}
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={applyNl}
              disabled={nlBusy}
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-white font-medium hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {nlBusy ? 'Reading…' : 'Apply'}
            </button>
            <button
              type="button"
              onClick={() => setNlMode(false)}
              className="text-slate-500 hover:text-slate-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          {renderInput()}
          {nlEligible && (
            <button
              type="button"
              onClick={() => setNlMode(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Type a sentence instead
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {onSubmit && (
        <div className="pt-2">
          <button
            type="button"
            onClick={onSubmit}
            className="rounded-lg bg-blue-600 px-6 py-3 text-white font-medium hover:bg-blue-700 transition"
          >
            Continue →
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Repeating group (sub-component)
// ---------------------------------------------------------------------------
// Renders the answer (an array of row objects keyed by sub-question
// field_path) as compact summary cards with an expanded edit form per row.
// Sub-question conditions are evaluated against the same row's values.
// ---------------------------------------------------------------------------

function summarizeRow(question: Question, row: RepeatingGroupRow): string {
  const schema = question.repeating_group;
  if (!schema) return '';
  const parts: string[] = [];
  for (const f of getVisibleRowFields(schema, row)) {
    const v = row[f.field_path];
    if (v === undefined || v === null || v === '') continue;
    let display: string;
    if (typeof v === 'boolean') {
      display = v ? 'Yes' : 'No';
    } else if (f.options) {
      const values = Array.isArray(v) ? v : [v];
      display = values
        .map((val) => f.options?.find((o) => o.value === val)?.label ?? String(val))
        .join(', ');
    } else if (f.field_type === 'currency') {
      display = `$${Number(v).toLocaleString()}`;
    } else {
      display = String(v);
    }
    parts.push(display);
    if (parts.length >= 3) break;
  }
  return parts.join(' · ');
}

function RepeatingGroupInput({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: RepeatingGroupRow[] | undefined;
  onChange: (v: RepeatingGroupRow[]) => void;
}) {
  const schema = question.repeating_group;
  const rows: RepeatingGroupRow[] = Array.isArray(value) ? value : [];
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | undefined>(undefined);

  if (!schema) {
    return <div className="text-red-600">Missing repeating_group schema</div>;
  }

  const atCap = schema.max_rows !== undefined && rows.length >= schema.max_rows;

  const addRow = () => {
    const next = [...rows, {}];
    onChange(next);
    setEditingIndex(next.length - 1);
    setRowError(undefined);
  };

  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next);
    if (editingIndex === i) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > i) setEditingIndex(editingIndex - 1);
    setRowError(undefined);
  };

  const updateRow = (i: number, fieldPath: string, v: unknown) => {
    const next = [...rows];
    next[i] = { ...next[i], [fieldPath]: v };
    onChange(next);
    setRowError(undefined);
  };

  const closeRow = (i: number) => {
    // Validate just this row's visible required sub-fields before collapsing.
    const row = rows[i] ?? {};
    for (const f of getVisibleRowFields(schema, row)) {
      const v = row[f.field_path];
      if (f.required && (v === undefined || v === null || v === '')) {
        setRowError(`"${f.text}" is required.`);
        return;
      }
    }
    setRowError(undefined);
    setEditingIndex(null);
  };

  return (
    <div className="space-y-3">
      {rows.map((row, i) =>
        editingIndex === i ? (
          // Expanded edit form. Rows are plain answer objects with no stable
          // id; position is the identity (add/remove re-renders are cheap).
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: rows keyed by position by design
            key={i}
            className="rounded-xl border-2 border-blue-200 bg-blue-50/30 p-4 sm:p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">
                {schema.row_label} {i + 1}
              </div>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-sm text-red-500 hover:text-red-700 font-medium"
              >
                Remove
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {getVisibleRowFields(schema, row).map((f) => (
                <div
                  key={f.id}
                  className={
                    ['textarea', 'address', 'multiselect', 'state_select'].includes(f.field_type)
                      ? 'sm:col-span-2'
                      : undefined
                  }
                >
                  <QuestionRenderer
                    question={f}
                    value={row[f.field_path]}
                    onChange={(v) => updateRow(i, f.field_path, v)}
                    compact
                  />
                </div>
              ))}
            </div>
            {rowError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {rowError}
              </div>
            )}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => closeRow(i)}
                className="rounded-lg bg-blue-600 px-5 py-2 text-sm text-white font-medium hover:bg-blue-700 transition"
              >
                Done with this {schema.row_label.toLowerCase()}
              </button>
            </div>
          </div>
        ) : (
          // Compact summary card
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: rows keyed by position by design
            key={i}
            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 transition"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {schema.row_label} {i + 1}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {summarizeRow(question, row) || 'Not filled in yet'}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setEditingIndex(i);
                  setRowError(undefined);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-sm text-slate-400 hover:text-red-600"
              >
                Remove
              </button>
            </div>
          </div>
        ),
      )}

      {!atCap && (
        <button
          type="button"
          onClick={addRow}
          className="w-full rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50/40 transition"
        >
          + {schema.add_button_label}
        </button>
      )}
      {atCap && (
        <p className="text-xs text-slate-500">
          Maximum of {schema.max_rows} {schema.row_label.toLowerCase()}s reached.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trade matrix (sub-component)
// ---------------------------------------------------------------------------

interface TradeRow {
  trade: string;
  employees?: number;
  payroll?: number;
}

function TradeMatrixInput({
  value,
  onChange,
}: {
  value: TradeRow[] | undefined;
  onChange: (v: TradeRow[]) => void;
}) {
  const rows: TradeRow[] = Array.isArray(value)
    ? value
    : [{ trade: '', employees: undefined, payroll: undefined }];
  const [_, setVer] = useState(0);

  const update = (i: number, patch: Partial<TradeRow>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, { trade: '', employees: undefined, payroll: undefined }]);
    setVer((v) => v + 1);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-700 px-2">
        <div className="col-span-6">Trade / Class Code</div>
        <div className="col-span-2"># Employees</div>
        <div className="col-span-4">Annual Payroll</div>
      </div>
      {rows.map((row, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows keyed by position by design
        <div key={i} className="grid grid-cols-12 gap-2">
          <input
            type="text"
            value={row.trade}
            onChange={(e) => update(i, { trade: e.target.value })}
            placeholder="e.g., 5403 Carpentry"
            className="col-span-6 rounded-md border border-slate-300 px-3 py-2"
          />
          <input
            type="number"
            value={row.employees ?? ''}
            onChange={(e) =>
              update(i, { employees: e.target.value ? Number(e.target.value) : undefined })
            }
            className="col-span-2 rounded-md border border-slate-300 px-3 py-2"
          />
          <div className="col-span-4 flex items-center gap-1">
            <span className="text-slate-500">$</span>
            <input
              type="number"
              value={row.payroll ?? ''}
              onChange={(e) =>
                update(i, { payroll: e.target.value ? Number(e.target.value) : undefined })
              }
              className="flex-1 rounded-md border border-slate-300 px-3 py-2"
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        + Add another trade
      </button>
    </div>
  );
}
